import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { issueBillingKey } from '../../api/paymentApi';

type Status = 'loading' | 'success' | 'error';

const BILLING_AUTH_DONE_PREFIX = 'policyIntel:billingAuthDone:';

type BillingIssueResult = Awaited<ReturnType<typeof issueBillingKey>>;

// React.StrictMode 개발환경의 effect 재실행에서도 동일 authKey 요청을 한 번만 보낸다.
// 첫 mount가 cleanup되어도 두 번째 mount가 같은 Promise를 이어받아 UI 상태를 갱신한다.
const billingIssueInFlight = new Map<string, Promise<BillingIssueResult>>();

function issueBillingKeyOnce(
  authKey: string,
  customerKey: string,
): Promise<BillingIssueResult> {
  const existing = billingIssueInFlight.get(authKey);
  if (existing) {
    return existing;
  }

  const request = issueBillingKey({ authKey, customerKey }).finally(() => {
    billingIssueInFlight.delete(authKey);
  });

  billingIssueInFlight.set(authKey, request);
  return request;
}

const BillingAuthCallbackPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState<Status>('loading');
  const [message, setMessage] = useState('자동결제 카드를 등록하고 있습니다.');

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);

  useEffect(() => {
    let mounted = true;

    const run = async () => {
      const authKey = params.get('authKey')?.trim() || '';
      const customerKey = params.get('customerKey')?.trim() || '';
      const code = params.get('code')?.trim() || '';
      const failMessage = params.get('message')?.trim() || '';

      if (code || failMessage) {
        if (mounted) {
          setStatus('error');
          setMessage(failMessage || `카드 등록 실패: ${code}`);
        }
        return;
      }

      if (!authKey || !customerKey) {
        if (mounted) {
          setStatus('error');
          setMessage('authKey 또는 customerKey가 누락되었습니다.');
        }
        return;
      }

      const doneKey = `${BILLING_AUTH_DONE_PREFIX}${authKey}`;

      if (window.sessionStorage.getItem(doneKey) === '1') {
        if (mounted) {
          setStatus('success');
          setMessage('자동결제 등록이 이미 처리되었습니다. 구독 상태를 확인합니다.');
          window.setTimeout(() => {
            navigate('/mypage?tab=billing&billing=registered', { replace: true });
          }, 500);
        }
        return;
      }

      try {
        const result = await issueBillingKeyOnce(authKey, customerKey);

        if (!mounted) {
          return;
        }

        if (!result.registered) {
          setStatus('error');
          setMessage(
            '카드 인증은 완료됐지만 최초 구독 결제 승인에 실패했습니다. 결제 내역을 확인한 뒤 다시 시도해 주세요.',
          );
          return;
        }

        window.sessionStorage.setItem(doneKey, '1');

        setStatus('success');
        setMessage('자동결제 카드 등록과 최초 구독 결제가 완료되었습니다.');

        window.setTimeout(() => {
          navigate('/mypage?tab=billing&billing=registered', { replace: true });
        }, 1200);
      } catch (e: any) {
        if (mounted) {
          setStatus('error');
          setMessage(
            e?.response?.data?.message ||
              e?.message ||
              '서버에서 자동결제 등록을 완료하지 못했습니다.',
          );
        }
      }
    };

    void run();
    return () => {
      mounted = false;
    };
  }, [navigate, params]);

  return (
    <Box sx={{ maxWidth: 560, mx: 'auto', mt: 8, px: 2 }}>
      <Typography variant="h5" fontWeight={900} gutterBottom>
        자동결제 카드 등록
      </Typography>

      {status === 'loading' ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 3 }}>
          <CircularProgress size={22} />
          <Typography>{message}</Typography>
        </Box>
      ) : (
        <Alert severity={status === 'success' ? 'success' : 'error'}>{message}</Alert>
      )}
    </Box>
  );
};

export default BillingAuthCallbackPage;
